import { FC, useLayoutEffect, useRef, useState, useEffect } from 'react';
import {
  Grid as KendoGrid,
  GridColumn,
  GridSortChangeEvent,
  GridToolbar,
  GridColumnResizeEvent,
  GridColumnProps as KendoGridColumnProps,
  GridColumnReorderEvent,
  GridCellProps,
  GridDetailExpandChangeEvent,
} from '@progress/kendo-react-grid';
import { SortDescriptor, orderBy } from '@progress/kendo-data-query';
import NumberCell from 'src/components/grid/NumberCell';
import { formatNumber } from 'src/utils/helperFunctions';
import { Grid, Typography, Box } from '@mui/material'; // MUI Grid
import ItemDetail from 'src/components/dashboard/overview/ItemDetail';
import { Order } from 'src/types/orderList';
import { gridColumnProperties } from 'src/types/gridColumnProperties';
import ImgCell from 'src/components/grid/ImageCell';
import useGlobals from 'src/hooks/useGlobals';
import { SETTING_KEYS } from 'src/constants';
import useSettings from 'src/hooks/useSettings';
import { DetailExpandDescriptor } from '@progress/kendo-react-data-tools';

interface Props {
  order: Order;
}

const OrderDetailGrid: FC<React.PropsWithChildren<Props>> = ({ order }) => {
  const LocalStorageKey = SETTING_KEYS.OpenDocumentDetailGridCols;
  const [sort, setSort] = useState<Array<SortDescriptor>>([{ field: 'lineNumber', dir: 'asc' }]);
  const { globals } = useGlobals();
  const getColumnsWithSettings = useSettings((state) => state.getColumnsWithSettings);
  const saveGridSettings = useSettings((state) => state.saveGridSettings);
  const settingsHaveLoaded = useSettings((state) => state.settingsHaveLoaded);

  // Initial column definitions using the correct 'cells' prop
  const getDefaultColumns = (): gridColumnProperties[] => [
    { field: 'lineNumber', title: 'Line #', width: 90 },
    { field: 'images', title: 'Image', sortable: false, cells: { data: ImgCell }, width: 80 },
    { field: 'itemNumber', title: 'Item Number', width: 140 },
    { field: 'description', title: 'Description', width: 300 },
    { field: 'quantity', title: 'Qty', format: '0', cells: { data: NumberCell }, width: 70 },
    { field: 'quantityShipped', title: 'Qty Ship/Res', format: '0', cells: { data: NumberCell }, width: 120 },
    { field: 'quantityBackordered', title: 'Qty BO', format: '0', cells: { data: NumberCell }, width: 80 },
    { field: 'quantityCancelled', title: 'Qty Cncl', format: '0', cells: { data: NumberCell }, width: 90 },
    { field: 'unitOfMeasure', title: 'UM', width: 70 },
    { field: 'price', title: 'Price', format: 'c2', cells: { data: NumberCell }, width: 80 },
    { field: 'total', title: 'Total', format: 'c2', cells: { data: NumberCell }, width: 80 },
    { field: 'cost', title: 'Cost', format: 'c2', cells: { data: NumberCell }, width: 80, display: !globals.customerPresent, hidden: globals.customerPresent },
    { field: 'margin', title: 'Margin', format: 'p2', cells: { data: NumberCell }, width: 80, display: !globals.customerPresent, hidden: globals.customerPresent },
    { field: 'disposition', title: 'Disp', width: 80 },
    { field: 'docNumber', title: 'Doc #', width: 80 },
    { field: '', title: '', width: '', editable: false, sortable: false, filterable: false, reorderable: false, display: true },
  ];

  const [columns, setColumns] = useState<gridColumnProperties[]>(getDefaultColumns());
  const [expandedDetailState, setExpandedDetailState] = useState<DetailExpandDescriptor>({});

  useEffect(() => {
    if (!settingsHaveLoaded) return;

    let colsFromSettings: gridColumnProperties[] = getColumnsWithSettings(LocalStorageKey, getDefaultColumns());

    colsFromSettings = colsFromSettings.map((loadedColumn: gridColumnProperties) => {
      const tempCol: any = { ...loadedColumn };
      if (typeof tempCol.cell === 'function' && !tempCol.cells) {
        tempCol.cells = { data: tempCol.cell as React.ComponentType<GridCellProps> };
      }
      if ('cell' in tempCol) {
        delete tempCol.cell;
      }
      if (tempCol.field === 'cost' || tempCol.field === 'margin') {
        tempCol.display = !globals.customerPresent;
        tempCol.hidden = globals.customerPresent;
      }
      return tempCol as gridColumnProperties;
    });
    setColumns(colsFromSettings);
  }, [settingsHaveLoaded, globals.customerPresent, LocalStorageKey, getColumnsWithSettings]);

  const isMounted = useRef(false);
  useLayoutEffect(() => {
    if (isMounted.current) {
      setColumns((prevCols) =>
        prevCols.map((c) => {
          if (c.field === 'cost' || c.field === 'margin') {
            return { ...c, hidden: globals.customerPresent, display: !globals.customerPresent };
          }
          return c;
        })
      );
    } else {
      isMounted.current = true;
    }
  }, [globals.customerPresent]);

  const handleDetailExpandChange = (event: GridDetailExpandChangeEvent) => {
    setExpandedDetailState(event.detailExpand);
  };

  const onColumnResize = (e: GridColumnResizeEvent) => {
    if (!e.end) return; //Only trigger on last update of slide

    let changedCol: gridColumnProperties = e.columns.find((c) => c.id === e.targetColumnId);

    let newCols = columns.map((c) => {
      if (c.field === changedCol.field) {
        if (e.nativeEvent.type === 'dblclick') {
          // on dblclick, set width to longest cell value
          let newWidth = Number(changedCol.width);
          return { ...c, width: newWidth < 100 ? 100 : newWidth };
        } else {
          // on resize, set width to newWidth
          return { ...c, width: e.newWidth };
        }
      }
      return { ...c };
    });

    setColumns(newCols);
    saveGridSettings(newCols, LocalStorageKey);
  };

  const onColumnReorder = (e: GridColumnReorderEvent) => {
    let cols = columns.map((c) => {
      let eventCol = e.columns.find((x) => x.field === c.field);
      if (eventCol) {
        return { ...c, orderIndex: eventCol.orderIndex };
      }
      return { ...c };
    });

    setColumns(cols);
    saveGridSettings(cols, LocalStorageKey);
  };

  return (
    <Box sx={{ minWidth: 500 }}>
      <KendoGrid
        data={orderBy(order.openOrderItems || [], sort)}
        scrollable="scrollable"
        dataItemKey="itemNumber"
        sortable={true}
        sort={sort}
        reorderable={true}
        resizable={true}
        onColumnResize={onColumnResize}
        onColumnReorder={onColumnReorder}
        onSortChange={(e: GridSortChangeEvent) => {
          setSort(e.sort);
        }}
        filterable={false}
        detail={ItemDetail}
        detailExpand={expandedDetailState} // Pass the new state object here
        onDetailExpandChange={handleDetailExpandChange} // Use the updated handler
        style={{ width: '100%' }}
      >
        <GridToolbar>
          <Typography color="textSecondary" variant="caption" component="div" sx={{ width: '100%' }}>
            <Grid container display="flex" justifyContent="space-between" flexWrap="wrap" flexGrow={1}>
              <Grid size={{ xs: 12 }}>
                {' '}
                {/* Ensure MUI Grid item prop is correct */}
                <Box sx={{ p: 1 }}>
                  <span>
                    <b>
                      Order Details ({order.orderNumber}-{formatNumber(order.orderSuffix, '00')})
                    </b>
                  </span>
                  <br />
                  <span>
                    <strong>Notes: </strong> {order.note}
                  </span>
                  <br />
                  <strong>Ship To: </strong> {order.shipToName}, {order.shipToAddress1} {order.shipToCity}, {order.shipToState} {order.shipToZip}{' '}
                  <span style={{ padding: '0 15px' }}>|</span>
                  <strong>Warehouse:</strong> {order.warehouseName} <span style={{ padding: '0 15px' }}>|</span>
                  <strong>Terms:</strong> {order.termsDescription}
                </Box>
              </Grid>
            </Grid>
          </Typography>
        </GridToolbar>
        {columns.map((column) => {
          const { display, filter, ...colProps } = column; // 'display' and 'filter' are your custom props
          if (colProps.hidden) return null; // Use Kendo's 'hidden' prop to control visibility
          return <GridColumn key={column.field || column.title /* Ensure a unique key */} {...colProps} />;
        })}
      </KendoGrid>
    </Box>
  );
};

export default OrderDetailGrid;
